import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { absUrl } from "@/lib/api";
import { Reveal, fadeUp } from "@/lib/motion";

const SHAPES = [
    { span: "md:col-span-8", aspect: "16 / 10" },
    { span: "md:col-span-4", aspect: "4 / 5" },
    { span: "md:col-span-4", aspect: "4 / 5" },
    { span: "md:col-span-4", aspect: "4 / 5" },
    { span: "md:col-span-8", aspect: "16 / 10" },
    { span: "md:col-span-4", aspect: "3 / 4" },
    { span: "md:col-span-4", aspect: "3 / 4" },
    { span: "md:col-span-4", aspect: "3 / 4" },
    { span: "md:col-span-12", aspect: "16 / 9" },
];

export function ProjectCard({ project, index, onOpen }) {
    const shape = SHAPES[index % SHAPES.length];
    const vidRef = useRef(null);
    const [hover, setHover] = useState(false);
    const hasVideo = !!project.video_url;

    const enter = () => {
        if (hasVideo) {
            vidRef.current?.play().catch(() => {});
            setHover(true);
        }
    };
    const leave = () => {
        if (vidRef.current) {
            vidRef.current.pause();
            vidRef.current.currentTime = 0;
        }
        setHover(false);
    };

    return (
        <motion.div variants={fadeUp} className={`group col-span-12 ${shape.span}`}>
            <button
                data-testid={`project-card-${project.slug}`}
                onClick={() => onOpen(project)}
                onMouseEnter={enter}
                onMouseLeave={leave}
                className="block w-full text-left cursor-pointer"
            >
                <div className="relative overflow-hidden bg-surface" style={{ aspectRatio: shape.aspect }}>
                    <img
                        src={absUrl(project.thumbnail_url)}
                        alt={project.title}
                        loading="lazy"
                        className={`absolute inset-0 h-full w-full object-cover transition-all duration-700 ease-out ${
                            hover && hasVideo ? "opacity-0 scale-105" : "opacity-100 scale-100 group-hover:scale-105"
                        }`}
                    />
                    {hasVideo && (
                        <video
                            ref={vidRef}
                            src={absUrl(project.video_url)}
                            poster={absUrl(project.thumbnail_url)}
                            muted
                            loop
                            playsInline
                            preload="none"
                            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${
                                hover ? "opacity-100" : "opacity-0"
                            }`}
                        />
                    )}
                    <div className="absolute top-4 left-4 text-[10px] font-bold tracking-[0.2em] text-white mix-blend-difference">
                        {String(index + 1).padStart(2, "0")}
                    </div>
                    <div
                        className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 ${
                            hover ? "opacity-100" : "opacity-0"
                        }`}
                    >
                        <span className="inline-flex items-center gap-2 bg-paper text-ink text-[10px] font-bold uppercase tracking-[0.2em] px-5 py-3">
                            <Play size={12} /> {hasVideo ? "Play film" : "View frame"}
                        </span>
                    </div>
                </div>
                <div className="flex items-baseline justify-between gap-4 pt-4">
                    <div>
                        <h3 className="font-display font-extrabold uppercase tracking-tight text-xl md:text-2xl">
                            {project.title}
                        </h3>
                        <p className="text-[11px] uppercase tracking-[0.16em] text-inkdim mt-1">
                            {project.client || project.category}
                            {project.client && project.category ? " — " : ""}
                            {project.client ? project.category : ""}
                        </p>
                    </div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-inkdim whitespace-nowrap">
                        {project.year}
                        {project.featured ? " · Featured" : ""}
                    </div>
                </div>
            </button>
        </motion.div>
    );
}

export default function WorkGrid({ projects, onOpen }) {
    return (
        <section id="work" className="px-5 md:px-10 py-24 md:py-32">
            <Reveal>
                <div className="flex items-end justify-between border-b border-ink pb-6">
                    <h2 className="font-display font-extrabold uppercase tracking-[-0.02em] text-4xl md:text-6xl">
                        Selected work
                    </h2>
                    <span className="font-serif italic text-2xl md:text-4xl text-inkdim">
                        {projects ? `(${String(projects.length).padStart(2, "0")})` : ""}
                    </span>
                </div>
            </Reveal>
            {!projects ? (
                <p className="py-24 text-xs uppercase tracking-[0.2em] text-inkdim" data-testid="work-loading">
                    Loading frames…
                </p>
            ) : projects.length === 0 ? (
                <p className="py-24 text-xs uppercase tracking-[0.2em] text-inkdim" data-testid="work-empty">
                    New work is being mounted — check back soon.
                </p>
            ) : (
                <motion.div
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.05 }}
                    className="mt-14 grid grid-cols-12 gap-x-6 md:gap-x-8 gap-y-16 md:gap-y-24"
                >
                    {projects.map((p, i) => (
                        <ProjectCard key={p.id} project={p} index={i} onOpen={onOpen} />
                    ))}
                </motion.div>
            )}
        </section>
    );
}
